import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { Helmet } from 'react-helmet';
import { tagService, Tag } from '../../services/tagService';
import { WordPressPost } from '../../types/wordpress';
import { MobileGridSkeleton, DesktopGridSkeleton } from '../SkeletonLoader';
// Removed: parallelTagsLoader, preloadManager imports (not used, causes massive API calls)
import { useSwipe } from '../SwipeProvider';
import { useResponsiveImage } from '../../hooks/useOptimizedImage';
import { useLanguageDetection } from '../../hooks/useLanguageDetection'; // ✅ NEW: Language detection
import { useLanguageContext } from '../../contexts/LanguageContext';
import { formatDate } from '../../utils/dateUtils';

// Device detection hook - FIXED with stability
const useDeviceType = () => {
  const [isMobile, setIsMobile] = useState(() => {
    // Initialize with proper mobile detection
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  });
  
  useEffect(() => {
    const checkDevice = () => {
      const newIsMobile = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      if (newIsMobile !== isMobile) {
        console.log('[useDeviceType] 📱💻 Device type change detected:', { 
          from: isMobile ? 'Mobile' : 'Desktop', 
          to: newIsMobile ? 'Mobile' : 'Desktop',
          screenWidth: window.innerWidth,
          userAgent: navigator.userAgent.substring(0, 50)
        });
        setIsMobile(newIsMobile);
      }
    };
    
    // ✅ MEMORY LEAK FIX: Use useRef to track timeout for proper cleanup
    let timeoutRef: NodeJS.Timeout | null = null;
    const debouncedCheck = () => {
      if (timeoutRef) {
        clearTimeout(timeoutRef);
      }
      timeoutRef = setTimeout(checkDevice, 100);
    };
    
    window.addEventListener('resize', debouncedCheck);
    return () => {
      window.removeEventListener('resize', debouncedCheck);
      // ✅ MEMORY LEAK FIX: Cleanup timeout on unmount
      if (timeoutRef) {
        clearTimeout(timeoutRef);
        timeoutRef = null;
      }
    };
  }, [isMobile]);
  
  return isMobile;
};

// ======================== PC LAYOUT (NewsFeed Copy) ========================
// Desktop News Feed Component - Modern Professional Design (COPIED FROM NEWSFEED)
const PCContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  /* ENSURE SCROLL CAPABILITY */
  width: 100%;
  overflow-x: hidden;
  overflow-y: visible; /* Allow vertical scrolling */
`;

const PCHeader = styled.header`
  padding: 40px 0;
  text-align: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  margin: 0 -24px 40px -24px;
  color: white;
  position: relative;
`;

const BackButton = styled.button`
  position: absolute;
  left: 24px;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  padding: 12px 16px;
  border-radius: 25px;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-50%) translateX(-2px);
  }
  
  &:active {
    transform: translateY(-50%) scale(0.95);
  }
`;

const PCHeaderTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  margin: 0;
  text-shadow: 0 2px 4px rgba(0,0,0,0.3);
  letter-spacing: -1px;
`;

const PCHeaderSubtitle = styled.p`
  font-size: 1.2rem;
  margin: 16px 0 0 0;
  opacity: 0.9;
  font-weight: 300;
`;

const PCFeedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  padding-bottom: 60px;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PCArticleCard = styled.div`
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #667eea, #764ba2);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover::before {
    opacity: 1;
  }
`;

// Modern Loading Animations (COPIED FROM NEWSFEED)
const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

const PCImageContainer = styled.div`
  position: relative;
  height: 240px;
  overflow: hidden;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
`;

const PCImagePlaceholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s infinite;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a0aec0;
  font-size: 2rem;
`;

const PCArticleImage = styled.img<{ $isLoaded?: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: all 0.3s ease;
  opacity: ${props => props.$isLoaded ? 1 : 0};
  transform: ${props => props.$isLoaded ? 'scale(1)' : 'scale(1.1)'};
  
  ${PCArticleCard}:hover & {
    transform: ${props => props.$isLoaded ? 'scale(1.05)' : 'scale(1.1)'};
  }
`;

const PCArticleContent = styled.div`
  padding: 24px;
`;

const PCArticleTitle = styled.h2`
  margin: 0 0 12px 0;
  font-size: 1.4rem;
  font-weight: 700;
  color: #2d3748;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const PCArticleExcerpt = styled.p`
  color: #718096;
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0 0 20px 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const PCArticleFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
`;

const PCPublishDate = styled.span`
  font-size: 0.85rem;
  color: #a0aec0;
  font-weight: 500;
`;

const PCReadMoreButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 25px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

// Loading More & End States (COPIED FROM NEWSFEED)
const PCLoadingMoreContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 20px;
  color: #718096;
  gap: 10px;
  font-size: 1.1rem;
  font-weight: 600;
`;

const PCLoadingSpinner = styled.div`
  width: 24px;
  height: 24px;
  border: 3px solid rgba(102, 126, 234, 0.1);
  border-top: 3px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const PCEndMessage = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #718096;
  font-size: 1.1rem;
  font-weight: 600;
`;

// Skeleton Loading Components (COPIED FROM NEWSFEED)
const PCSkeletonCard = styled.div<{ delay?: number }>`
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  animation: ${fadeInUp} 0.6s ease-out;
  animation-delay: ${props => props.delay || 0}s;
  animation-fill-mode: both;
`;

const PCSkeletonImage = styled.div`
  height: 240px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s infinite;
`;

const PCSkeletonContent = styled.div`
  padding: 24px;
`;

const PCSkeletonTitle = styled.div`
  height: 24px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s infinite;
  border-radius: 6px;
  margin-bottom: 12px;
`;

const PCSkeletonTitleSecond = styled.div`
  height: 24px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s infinite;
  animation-delay: 0.1s;
  border-radius: 6px;
  margin-bottom: 20px;
  width: 75%;
`;

const PCSkeletonText = styled.div<{ width?: string; delay?: number }>`
  height: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s infinite;
  animation-delay: ${props => props.delay || 0}s;
  border-radius: 4px;
  margin-bottom: 8px;
  width: ${props => props.width || '100%'};
`;

const PCSkeletonFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 20px;
`;

const PCSkeletonDate = styled.div`
  height: 14px;
  width: 120px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s infinite;
  animation-delay: 0.3s;
  border-radius: 4px;
`;

const PCSkeletonButton = styled.div`
  height: 40px;
  width: 100px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s infinite;
  animation-delay: 0.4s;
  border-radius: 20px;
`;

// PC LazyImage Component (COPIED FROM NEWSFEED)
interface PCLazyImageProps {
  src: string;
  alt: string;
  placeholder?: string;
  context?: 'post-card' | 'featured-card';
  isMobile?: boolean;
}

const PCLazyImage: React.FC<PCLazyImageProps> = ({ 
  src, 
  alt, 
  placeholder = '🖼️', 
  context = 'post-card',
  isMobile = false 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  
  // Use optimized image based on context
  const optimizedImage = useResponsiveImage(src, context, isMobile);

  const handleLoad = () => {
    setIsLoaded(true);
    setHasError(false);
  };

  const handleError = () => {
    console.warn('Optimized image failed, fallback to original:', optimizedImage.src);
    setHasError(true);
    setIsLoaded(true);
  };

  const imageSrc = hasError ? 'https://cdn.infonium.org/img/logo_infonium.png' : optimizedImage.src;

  return (
    <PCImageContainer>
      {!isLoaded && (
        <PCImagePlaceholder>
          {placeholder}
        </PCImagePlaceholder>
      )}
      <PCArticleImage
        src={imageSrc}
        alt={alt}
        onLoad={handleLoad}
        onError={handleError}
        $isLoaded={isLoaded}
        loading="lazy"
        title={`${alt} - Optimized: ${optimizedImage.isOptimized ? optimizedImage.size : 'Original'} (${context})`}
      />
    </PCImageContainer>
  );
};

// PC Skeleton Loader Component (COPIED FROM NEWSFEED)
const PCSkeletonLoader = () => (
  <PCFeedGrid>
    {Array.from({ length: 6 }, (_, index) => (
      <PCSkeletonCard key={index} delay={index * 0.1}>
        <PCSkeletonImage />
        <PCSkeletonContent>
          <PCSkeletonTitle />
          <PCSkeletonTitleSecond />
          <PCSkeletonText delay={0.2} />
          <PCSkeletonText width="85%" delay={0.25} />
          <PCSkeletonText width="60%" delay={0.3} />
          <PCSkeletonFooter>
            <PCSkeletonDate />
            <PCSkeletonButton />
          </PCSkeletonFooter>
        </PCSkeletonContent>
      </PCSkeletonCard>
    ))}
  </PCFeedGrid>
);

// ======================== MOBILE LAYOUT (Grid Layout) ========================
const MobileContainer = styled.div<{ isVisible?: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #000;
  color: #fff;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  transform: ${({ isVisible = true }) => isVisible ? 'translateX(0)' : 'translateX(100%)'};
  transition: transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  overflow: hidden;
  
  /* Enhanced layer feel */
  box-shadow: ${({ isVisible = true }) => isVisible ? '-10px 0 30px rgba(0, 0, 0, 0.5)' : 'none'};
  will-change: transform;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  
  /* Smooth hardware acceleration */
  -webkit-transform: ${({ isVisible = true }) => isVisible ? 'translateX(0) translateZ(0)' : 'translateX(100%) translateZ(0)'};
  
  /* Mobile scroll optimizations for content area */
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
  overscroll-behavior: contain;
  touch-action: auto; /* Changed from pan-y to auto to allow swipe gestures */
`;

const MobileHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 100;
  background: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(10px);
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  gap: 12px;
`;

const MobileBackButton = styled.button`
  background: none;
  border: none;
  color: #fff;
  font-size: 24px;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.3s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const TagInfo = styled.div`
  flex: 1;
`;

const TagName = styled.h1`
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  color: #fff;
`;

const PostCount = styled.div`
  font-size: 14px;
  color: #aaa;
  margin: 0;
`;

// ======================== MOBILE RESPONSIVE GRID (VIEWPORT-BASED 3x3) ========================
const MobilePostGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1px; /* Minimal gap for clean look */
  padding: 0;
  margin: 0;
  width: 100%;
  background: #000;
  flex: 1;
  overflow-y: auto;
  
  /* Calculate grid item height based on viewport */
  /* Each row = (viewport height - header) / 3 */
  grid-auto-rows: calc((100vh - 60px) / 3);
  place-items: stretch;
  
  /* Smooth scrolling on mobile */
  -webkit-overflow-scrolling: touch;
  
  /* Ensure grid items fill exactly 1/3 of viewport */
  & > * {
    height: calc((100vh - 60px) / 3);
    min-height: calc((100vh - 60px) / 3);
    max-height: calc((100vh - 60px) / 3);
  }
`;

const MobilePostGridItem = styled.div`
  /* Fixed viewport-based dimensions - exactly 1/3 of viewport */
  width: 100%;
  height: calc((100vh - 60px) / 3); /* Exact 1/3 of available viewport */
  position: relative;
  cursor: pointer;
  overflow: hidden;
  background: #1a1a1a;
  user-select: none;
  -webkit-tap-highlight-color: rgba(255, 255, 255, 0.1);
  
  /* Enhanced touch handling */
  touch-action: auto;
  pointer-events: auto;
  
  /* Enhanced mobile interaction */
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  
  /* Visual feedback states */
  transition: background 0.15s ease, transform 0.1s ease;
  
  &:hover {
    cursor: pointer;
    background: #2a2a2a;
  }
  
  &:active {
    transform: scale(0.98);
    background: #2a2a2a;
  }
  
  &:focus {
    outline: 2px solid rgba(255, 255, 255, 0.5);
    outline-offset: 2px;
  }
  
  &.scrolling {
    pointer-events: none;
  }
`;

const MobilePostThumbnailImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  pointer-events: none !important;
  user-select: none;
  -webkit-user-drag: none;
  -khtml-user-drag: none;
  -moz-user-drag: none;
  -o-user-drag: none;
  user-drag: none;
  
  /* Enhanced image quality */
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  
  /* Smooth loading transition */
  opacity: 1;
  transition: opacity 0.3s ease;
  
  /* Prevent blurry upscaling */
  image-rendering: auto;
  -ms-interpolation-mode: bicubic;
  
  /* Loading state */
  &[src=""], &:not([src]) {
    opacity: 0.3;
    background: #2a2a2a;
  }
  
  /* Error state styling */
  &[src$="logo_infonium.png"] {
    object-fit: contain;
    background: #1a1a1a;
    padding: 20%;
  }
`;

// Optimized Mobile Thumbnail Component
interface MobilePostThumbnailProps {
  src: string;
  alt: string;
  loading?: 'lazy' | 'eager';
  onError?: (e: React.SyntheticEvent<HTMLImageElement>) => void;
}

const MobilePostThumbnail: React.FC<MobilePostThumbnailProps> = ({ 
  src, 
  alt, 
  loading = 'lazy',
  onError 
}) => {
  const optimizedImage = useResponsiveImage(src, 'mobile-thumbnail', true);
  
  const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    console.warn('Mobile thumbnail failed, fallback to original:', optimizedImage.src);
    if (onError) {
      onError(e);
    } else {
      const target = e.target as HTMLImageElement;
      target.src = 'https://cdn.infonium.org/img/logo_infonium.png';
    }
  };

  return (
    <MobilePostThumbnailImg
      src={optimizedImage.src}
      alt={alt}
      loading={loading}
      onError={handleError}
      title={`${alt} - Optimized: ${optimizedImage.isOptimized ? optimizedImage.size : 'Original'} (mobile-thumbnail)`}
    />
  );
};

const MobilePostOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
  padding: clamp(4px, 1.5vw, 8px);
  pointer-events: none;
`;

const MobilePostTitle = styled.div`
  color: #fff;
  font-size: clamp(8px, 2.5vw, 12px); /* Responsive font size */
  font-weight: 600;
  line-height: 1.2;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const MobilePostMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: clamp(7px, 2vw, 10px); /* Responsive font size */
  color: #ccc;
  margin-top: 2px;
`;

const MobileAudioIndicator = styled.span`
  &:before {
    content: '🎵';
    font-size: clamp(6px, 1.8vw, 9px);
  }
`;

const MobileScrollToTopButton = styled.button<{ visible: boolean }>`
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: clamp(45px, 12vw, 60px); /* Responsive size */
  height: clamp(45px, 12vw, 60px);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  cursor: pointer;
  z-index: 1000;
  display: ${({ visible }) => visible ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

// Transition overlay to cover navigation frame
const NavigationOverlay = styled.div<{ isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #000;
  z-index: 99999; /* Higher than container */
  opacity: ${({ isVisible }) => isVisible ? 1 : 0};
  visibility: ${({ isVisible }) => isVisible ? 'visible' : 'hidden'};
  transition: opacity 0.2s ease-out, visibility 0.2s ease-out;
  pointer-events: none;
`;

// ======================== SHARED COMPONENTS ========================
// Shared Loading/Error Components
const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background: #000;
  color: #fff;
  font-size: 18px;
  flex-direction: column;
  gap: 16px;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top: 3px solid #fff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background: #000;
  color: #fff;
  text-align: center;
  padding: 20px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 60px 20px;
  text-align: center;
  color: #aaa;
`;

// ======================== PC COMPONENT (NewsFeed Style) ========================
const PCTagFeed: React.FC<{
  tag: Tag;
  posts: WordPressPost[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  totalCount: number;
  onLoadMore: () => void;
  onPostClick: (post: WordPressPost) => void;
  onClose: () => void;
}> = ({ tag, posts, loading, loadingMore, hasMore, totalCount, onLoadMore, onPostClick, onClose }) => {
  const navigate = useNavigate();

  console.log('[PCTagFeed] 💻 Component rendering started');
  console.log('[PCTagFeed] 💻 Props received:', {
    tagName: tag?.name,
    tagId: tag?.id,
    postsCount: posts?.length,
    loading,
    loadingMore,
    hasMore
  });

  // Infinite scroll handler (EXACT COPY FROM NEWSFEED)
  useEffect(() => {
    console.log('[PCTagFeed] 💻 Setting up scroll listener, current state:', {
      loading, loadingMore, hasMore, postsCount: posts?.length,
      tagName: tag?.name
    });
    
    const handleScroll = () => {
      if (loading || loadingMore || !hasMore) {
        console.log('[PCTagFeed] 💻 PC scroll blocked:', { loading, loadingMore, hasMore });
        return;
      }
      
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      console.log('[PCTagFeed] 💻 PC scroll debug (detailed):', {
        scrollTop, windowHeight, documentHeight,
        bodyHeight: document.body.scrollHeight,
        containerHeight: document.getElementById('root')?.scrollHeight || 0,
        threshold: 200,
        remaining: documentHeight - (scrollTop + windowHeight),
        shouldTrigger: scrollTop + windowHeight >= documentHeight - 200,
        postsCount: posts?.length,
        hasMore,
        loadingMore
      });
      
      // 🚀 PC OPTIMIZATION: Load more when user is 800px from bottom (much earlier for smooth scrolling)
      if (scrollTop + windowHeight >= documentHeight - 800) {
        console.log('[PCTagFeed] 💻 PC scroll trigger - calling onLoadMore');
        onLoadMore();
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    console.log('[PCTagFeed] 💻 Window scroll listener attached');
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      console.log('[PCTagFeed] 💻 Window scroll listener removed');
    };
  }, [loading, loadingMore, hasMore, onLoadMore, posts, tag]);

  // Utility functions (COPIED FROM NEWSFEED)
  const cleanHtml = (html: string) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || '';
  };

  // Remove local formatDate function - using imported one from dateUtils

  const getPostThumbnail = (post: WordPressPost): string => {
    if (post.imageUrls && post.imageUrls.length > 0) {
      return post.imageUrls[0];
    }
    return 'https://cdn.infonium.org/img/logo_infonium.png';
  };

  if (loading && posts.length === 0) {
    return (
      <PCContainer>
        <PCHeader>
          <BackButton onClick={onClose} title="Go Back">
            ← Go Back
          </BackButton>
          <PCHeaderTitle>#{tag.name}</PCHeaderTitle>
          <PCHeaderSubtitle>Loading...</PCHeaderSubtitle>
        </PCHeader>
        <PCSkeletonLoader />
      </PCContainer>
    );
  }

  if (posts.length === 0 && !loading) {
    return (
      <PCContainer>
        <PCHeader>
          <BackButton onClick={onClose} title="Go Back">
            ← Go Back
          </BackButton>
          <PCHeaderTitle>#{tag.name}</PCHeaderTitle>
          <PCHeaderSubtitle>No articles</PCHeaderSubtitle>
        </PCHeader>
        <EmptyState>
          <div style={{ fontSize: '4rem', marginBottom: '20px' }}>🏷️</div>
                      <h2 style={{ margin: '0 0 10px 0', color: '#2d3748' }}>No articles yet</h2>
          <p>Try another tag or return to homepage</p>
        </EmptyState>
      </PCContainer>
    );
  }

  return (
    <PCContainer>
      <PCHeader>
        <BackButton onClick={onClose} title="Go Back">
          ← Go Back
        </BackButton>
        <PCHeaderTitle>#{tag.name}</PCHeaderTitle>
                        <PCHeaderSubtitle>{totalCount} articles</PCHeaderSubtitle>
      </PCHeader>
      
      <PCFeedGrid>
        {/* Real posts */}
        {posts.map(post => {
          const title = post.title.rendered ? cleanHtml(post.title.rendered) : '';
          const excerpt = post.excerpt?.rendered ? cleanHtml(post.excerpt.rendered) : 'Discover this interesting content...';
          const mainImageUrl = getPostThumbnail(post);

          return (
            <PCArticleCard key={post.id} onClick={() => onPostClick(post)} data-testid="post-card">
              <PCLazyImage 
                src={mainImageUrl} 
                alt={title} 
                placeholder="📰" 
                context="post-card"
                isMobile={false}
              />
              
              <PCArticleContent>
                <PCArticleTitle>{title}</PCArticleTitle>
                <PCArticleExcerpt>
                  {excerpt.length > 150 ? `${excerpt.substring(0, 150)}...` : excerpt}
                </PCArticleExcerpt>
                
                <PCArticleFooter>
                  <PCPublishDate>{formatDate(post.date)}</PCPublishDate>
                  <PCReadMoreButton onClick={(e) => {
                    e.stopPropagation();
                    onPostClick(post);
                  }}>
                    Read More
                  </PCReadMoreButton>
                </PCArticleFooter>
              </PCArticleContent>
            </PCArticleCard>
          );
        })}
        
        {/* Skeleton for remaining posts during loading */}
        {loading && 
          Array.from({ length: Math.max(0, 12 - posts.length) }, (_, index) => (
            <PCArticleCard key={`skeleton-${index}`} style={{
              background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
              backgroundSize: '200px 100%',
              animation: 'shimmer 1.5s infinite',
              minHeight: '400px'
            }}>
              <div style={{ 
                height: '240px',
                background: 'linear-gradient(90deg, #e0e0e0 25%, #d0d0d0 50%, #e0e0e0 75%)',
                backgroundSize: '200px 100%',
                animation: 'shimmer 1.5s infinite'
              }} />
              <div style={{ padding: '24px' }}>
                <div style={{ 
                  height: '24px', 
                  background: 'linear-gradient(90deg, #e0e0e0 25%, #d0d0d0 50%, #e0e0e0 75%)',
                  backgroundSize: '200px 100%',
                  animation: 'shimmer 1.5s infinite',
                  marginBottom: '12px',
                  borderRadius: '4px'
                }} />
                <div style={{ 
                  height: '16px', 
                  background: 'linear-gradient(90deg, #e0e0e0 25%, #d0d0d0 50%, #e0e0e0 75%)',
                  backgroundSize: '200px 100%',
                  animation: 'shimmer 1.5s infinite',
                  marginBottom: '8px',
                  borderRadius: '4px',
                  width: '80%'
                }} />
                <div style={{ 
                  height: '14px', 
                  background: 'linear-gradient(90deg, #e0e0e0 25%, #d0d0d0 50%, #e0e0e0 75%)',
                  backgroundSize: '200px 100%',
                  animation: 'shimmer 1.5s infinite',
                  borderRadius: '4px',
                  width: '60%'
                }} />
              </div>
            </PCArticleCard>
          ))
        }
      </PCFeedGrid>
      
      {/* Progressive loading indicator */}
      {loading && posts.length > 0 && (
        <div style={{
          textAlign: 'center',
          padding: '20px',
          color: '#666',
          fontSize: '16px'
        }}>
          📦 Loading more articles... ({posts.length} loaded)
        </div>
      )}
      
      {/* CSS animations for enhanced UX */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200px 0; }
          100% { background-position: calc(200px + 100%) 0; }
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes fadeInScale {
          0% {
            opacity: 0;
            transform: scale(0.9) translateY(10px);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
      `}</style>

      {/* 💻 PC LOADING INDICATOR: Enhanced design for better UX */}
      {loadingMore && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '30px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '15px',
          margin: '20px auto',
          maxWidth: '450px',
          color: '#fff',
          boxShadow: '0 12px 30px rgba(102, 126, 234, 0.3)',
          animation: 'fadeInScale 0.3s ease-out'
        }}>
          <div style={{
            width: '22px',
            height: '22px',
            border: '3px solid rgba(255,255,255,0.3)',
            borderTop: '3px solid #fff',
            borderRadius: '50%',
            marginRight: '12px',
            animation: 'spin 1s linear infinite'
          }}></div>
          <span style={{ 
            fontSize: '16px',
            fontWeight: '600',
            letterSpacing: '0.5px'
          }}>
            📦 Loading more articles for tag #{tag.name}...
          </span>
        </div>
      )}

      {/* End of content indicator removed - not needed */}
    </PCContainer>
  );
};

// ======================== MOBILE COMPONENT (Grid Layout) ========================
const MobileTagGrid: React.FC<{
  tag: Tag;
  posts: WordPressPost[];
  loading?: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  totalCount: number;
  onLoadMore: () => void;
  onPostClick: (post: WordPressPost) => void;
  onClose: () => void;
  isVisible?: boolean;
}> = ({ tag, posts, loading = false, loadingMore, hasMore, totalCount, onLoadMore, onPostClick, onClose, isVisible = true }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  
  // Swipe-to-close state
  const [isSwipeTracking, setIsSwipeTracking] = useState(false);
  const [swipeStartX, setSwipeStartX] = useState(0);
  const [swipeStartY, setSwipeStartY] = useState(0);
  const [swipeProgress, setSwipeProgress] = useState(0);
  
  // Touch gesture tracking for post interactions (separate from page swipe)
  const touchDataRef = useRef<{
    startX: number;
    startY: number;
    startTime: number;
    moved: boolean;
    threshold: number;
  }>({
    startX: 0,
    startY: 0,
    startTime: 0,
    moved: false,
    threshold: 10
  });

  // Mobile scroll detection with infinite loading restored
  useEffect(() => {
    const handleScroll = () => {
      if (!scrollRef.current || loadingMore || !hasMore) return;
      
      const container = scrollRef.current;
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;
      
      setShowScrollTop(scrollTop > 500);
      
      // Enhanced mobile preloading with dynamic threshold
      const remainingHeight = scrollHeight - (scrollTop + clientHeight);
      const totalHeight = scrollHeight;
      const viewportHeight = clientHeight;
      
      let threshold = 200;
      if (totalHeight > viewportHeight * 3) {
        threshold = 300;
      } else if (totalHeight < viewportHeight * 2) {
        threshold = 150;
      }
      
      if (remainingHeight <= threshold) {
        console.log('[MobileTagGrid] 🚀 Smart mobile preloading triggered:', {
          scrollTop, 
          scrollHeight, 
          clientHeight, 
          remainingHeight,
          threshold,
          totalPosts: posts.length
        });
        onLoadMore();
      }
    };

    const handleTouchEnd = () => {
      setTimeout(() => handleScroll(), 100);
    };

    const container = scrollRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
      container.addEventListener('touchend', handleTouchEnd, { passive: true });
      container.addEventListener('touchcancel', handleTouchEnd, { passive: true });
      
      return () => {
        container.removeEventListener('scroll', handleScroll);
        container.removeEventListener('touchend', handleTouchEnd);
        container.removeEventListener('touchcancel', handleTouchEnd);
      };
    }
  }, [loadingMore, hasMore, onLoadMore, posts.length]);

  // Swipe-to-close handlers for page navigation
  const handlePageTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    setIsSwipeTracking(true);
    setSwipeStartX(touch.clientX);
    setSwipeStartY(touch.clientY);
    setSwipeProgress(0);
    
    console.log('[TagPage] 👆 Page swipe tracking started:', { 
      startX: touch.clientX, 
      startY: touch.clientY 
    });
  };

  const handlePageTouchMove = (e: React.TouchEvent) => {
    if (!isSwipeTracking) return;
    
    const touch = e.touches[0];
    const deltaX = touch.clientX - swipeStartX;
    const deltaY = Math.abs(touch.clientY - swipeStartY);
    
    // Only track right swipes (positive deltaX) and prevent if vertical scroll
    if (deltaX > 0 && deltaY < 50) {
      const progress = Math.min(deltaX / window.innerWidth, 1);
      setSwipeProgress(progress);
      
      // Enhanced visual feedback like main feed
      if (containerRef.current) {
        const translateX = deltaX; // Use actual pixel distance
        const opacity = 1 - (progress * 0.4); // Less opacity change
        const scale = 1 - (progress * 0.03); // More subtle scale
        const blur = progress * 5; // Add blur effect
        
        containerRef.current.style.transform = `translateX(${translateX}px) scale(${scale}) translateZ(0)`;
        containerRef.current.style.opacity = `${opacity}`;
        containerRef.current.style.filter = `blur(${blur}px)`;
        containerRef.current.style.borderRadius = `${progress * 15}px 0 0 ${progress * 15}px`;
        containerRef.current.style.boxShadow = `-${progress * 20}px 0 ${30 + progress * 20}px rgba(0, 0, 0, ${0.3 + progress * 0.4})`;
      }
      
      console.log('[TagPage] 👆 Enhanced page swipe in progress:', { 
        deltaX, 
        progress: progress.toFixed(2),
        translateX: deltaX
      });
    } else if (deltaY >= 50) {
      // Cancel swipe if user is scrolling vertically
      resetPageSwipeVisualFeedback();
      setIsSwipeTracking(false);
    }
  };

  const handlePageTouchEnd = (e: React.TouchEvent) => {
    if (!isSwipeTracking) return;
    
    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - swipeStartX;
    const swipeDistance = Math.abs(deltaX);
    const swipeVelocity = swipeDistance / 200;
    
    setIsSwipeTracking(false);
    
    // Determine if swipe should close the page
    const shouldClose = (deltaX > 120) || (deltaX > 60 && swipeVelocity > 0.8);
    
    console.log('[TagPage] 👆 Page swipe ended:', { 
      deltaX, 
      swipeDistance, 
      swipeVelocity: swipeVelocity.toFixed(2), 
      shouldClose,
      progress: swipeProgress.toFixed(2)
    });
    
    if (shouldClose) {
      // Close with enhanced animation
      console.log('[TagPage] ✅ Swipe-to-close triggered!');
      handleVisualClose();
    } else {
      // Snap back with smooth animation
      console.log('[TagPage] 🔙 Snapping back to position');
      resetPageSwipeVisualFeedback();
    }
  };

  const resetPageSwipeVisualFeedback = () => {
    if (containerRef.current) {
      containerRef.current.style.transition = 'transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.25s ease-out, border-radius 0.25s ease-out, filter 0.25s ease-out, box-shadow 0.25s ease-out';
      containerRef.current.style.transform = '';
      containerRef.current.style.opacity = '';
      containerRef.current.style.borderRadius = '';
      containerRef.current.style.filter = '';
      containerRef.current.style.boxShadow = '';
      
      // Clean up transition after animation
      setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.style.transition = '';
        }
      }, 250); // Reduced timing
    }
    setSwipeProgress(0);
  };

  // Local visual close animation - delegates to main component
  const handleVisualClose = () => {
    console.log('[MobileTagGrid] 🔙 Visual close animation triggered');
    if (containerRef.current) {
      containerRef.current.style.transition = 'transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.25s ease-out, filter 0.25s ease-out';
      containerRef.current.style.transform = 'translateX(100%) translateZ(0)';
      containerRef.current.style.opacity = '0';
      containerRef.current.style.filter = 'blur(5px)';
      
      setTimeout(() => {
        onClose(); // This calls main component's handleEnhancedClose with proper navigation
        resetPageSwipeVisualFeedback();
      }, 250); // Reduced timing for faster transition
    } else {
      onClose();
    }
  };

  // Reset visual feedback when page becomes visible
  useEffect(() => {
    if (isVisible) {
      resetPageSwipeVisualFeedback();
    }
  }, [isVisible]);



  const scrollToTop = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getPostThumbnail = (post: WordPressPost): string => {
    if (post.imageUrls && post.imageUrls.length > 0) {
      return post.imageUrls[0];
    }
    return 'https://cdn.infonium.org/img/logo_infonium.png';
  };

  const cleanTitle = (html: string): string => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || '';
  };

  // Remove local formatDate function - using imported one from dateUtils

  // Post touch handlers (separate from page swipe)
  const handleTouchStart = (e: React.TouchEvent, post: WordPressPost) => {
    const touch = e.touches[0];
    touchDataRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      startTime: Date.now(),
      moved: false,
      threshold: 10
    };
    
    console.log('[MobileTagGrid] 📱 Post TouchStart:', { 
      postId: post.id, 
      coordinates: { x: touch.clientX, y: touch.clientY }
    });
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchDataRef.current.startTime) return;
    
    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - touchDataRef.current.startX);
    const deltaY = Math.abs(touch.clientY - touchDataRef.current.startY);
    
    if (deltaX > touchDataRef.current.threshold || deltaY > touchDataRef.current.threshold) {
      touchDataRef.current.moved = true;
    }
  };

  const handleTouchEnd = (e: React.TouchEvent, post: WordPressPost) => {
    const touchDuration = Date.now() - touchDataRef.current.startTime;
    const wasMoved = touchDataRef.current.moved;
    
    console.log('[MobileTagGrid] 📱 Post TouchEnd Analysis:', { 
      postId: post.id,
      duration: touchDuration,
      moved: wasMoved,
      decision: !wasMoved && touchDuration < 500 ? 'VALID_TAP' : 'IGNORE_SWIPE'
    });
    
    if (!wasMoved && touchDuration < 500) {
      console.log('[MobileTagGrid] ✅ Valid tap detected, navigating to post:', post.id);
      onPostClick(post);
    } else {
      console.log('[MobileTagGrid] 🚫 Swipe/drag detected, ignoring tap:', { 
        postId: post.id, 
        reason: wasMoved ? 'moved' : 'too_long' 
      });
    }
    
    // Reset touch data
    touchDataRef.current = {
      startX: 0,
      startY: 0,
      startTime: 0,
      moved: false,
      threshold: 10
    };
  };

  return (
    <MobileContainer 
      isVisible={isVisible}
      ref={containerRef}
      onTouchStart={handlePageTouchStart}
      onTouchMove={handlePageTouchMove}
      onTouchEnd={handlePageTouchEnd}
    >
      <MobileHeader data-header="true">
        <MobileBackButton onClick={handleVisualClose} title="Quay lại">
          ←
        </MobileBackButton>
        <TagInfo>
          <TagName>#{tag.name}</TagName>
          <PostCount>
                          {totalCount} articles
          </PostCount>
        </TagInfo>
      </MobileHeader>

      {posts.length === 0 && !loading ? (
        <EmptyState>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏷️</div>
          <div style={{ fontSize: '18px', marginBottom: '8px' }}>
            No articles with this tag yet
          </div>
          <div style={{ fontSize: '14px' }}>
            Try another tag or return to homepage
          </div>
        </EmptyState>
      ) : (
        <>
          <MobilePostGrid ref={scrollRef}>
            {posts.map((post, index) => (
              <MobilePostGridItem
                key={post.id}
                onTouchStart={(e) => handleTouchStart(e, post)}
                onTouchMove={handleTouchMove}
                onTouchEnd={(e) => handleTouchEnd(e, post)}
                data-testid="mobile-post-card"
              >
                <MobilePostThumbnail
                  src={getPostThumbnail(post)}
                  alt={cleanTitle(post.title.rendered)}
                  loading="lazy"
                />
                <MobilePostOverlay>
                  <MobilePostTitle>{cleanTitle(post.title.rendered)}</MobilePostTitle>
                  <MobilePostMeta>
                    {post.audioUrl && <MobileAudioIndicator />}
                    <span>{formatDate(post.date)}</span>
                  </MobilePostMeta>
                </MobilePostOverlay>
              </MobilePostGridItem>
            ))}
          </MobilePostGrid>

          {/* Loading more indicator during infinite scroll */}
          {loadingMore && (
            <div style={{
              textAlign: 'center',
              padding: '20px',
              color: '#666',
              fontSize: '14px'
            }}>
              📦 Loading more articles...
            </div>
          )}

          {/* End message removed to avoid covering content */}
        </>
      )}
      
      {/* Add shimmer animation */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200px 0; }
          100% { background-position: calc(200px + 100%) 0; }
        }
      `}</style>

      <MobileScrollToTopButton visible={showScrollTop} onClick={scrollToTop} title="Lên đầu trang">
        ↑
      </MobileScrollToTopButton>
    </MobileContainer>
  );
};

// ======================== MAIN TAGPAGE COMPONENT ========================
const TagPage: React.FC = () => {
  const { slug, postId } = useParams<{ slug: string; postId?: string }>();
  const tagSlug = slug; // Keep tagSlug for backward compatibility with existing logic
  const navigate = useNavigate();
  const isMobile = useDeviceType();
  const { currentPost, setCurrentPost } = useSwipe();
  
  // ✅ NEW: Language detection with all required functions and debugging
  const { getCurrentLanguageCategoryId, isInitialized, languageContext } = useLanguageDetection();
  
  // 🚨 CRITICAL: Test language detection immediately
  React.useEffect(() => {
    const testLanguageDetection = () => {
      const languageId = getCurrentLanguageCategoryId();
      const context = languageContext;
      
      console.log('[TagPage] 🚨 IMMEDIATE LANGUAGE TEST:', {
        isInitialized,
        languageId,
        context,
        shouldHaveLanguageId: isInitialized,
        isProperlyConfigured: isInitialized && !!languageId
      });
      
      if (isInitialized && !languageId) {
        console.error('[TagPage] 🚨 CRITICAL ERROR: Language detection initialized but no language ID!');
        console.error('[TagPage] 🚨 This will cause NO LANGUAGE FILTERING!');
      }
    };
    
    testLanguageDetection();
  }, [isInitialized, getCurrentLanguageCategoryId, languageContext]);
  
  // State management
  const [tag, setTag] = useState<Tag | null>(null);
  const [posts, setPosts] = useState<WordPressPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [isVisible, setIsVisible] = useState(true);
  const [showNavigationOverlay, setShowNavigationOverlay] = useState(false);
  const [totalCount, setTotalCount] = useState<number>(0); // NEW: Accurate language-specific count
  const perPage = isMobile ? 30 : 12;

  console.log('[TagPage] 📱💻 Device detection + URL Context:', { 
    isMobile, 
    tagSlug,
    page,
    perPage,
    layout: isMobile ? 'Mobile Grid' : 'PC NewsFeed',
    context: tagSlug ? 'Tag Grid/Feed' : 'Tag Grid/Feed'
  });

  // Load tag data - FIXED: Wait for language detection and include in dependencies
  useEffect(() => {
    if (!tagSlug || !isInitialized) {
      console.log('[TagPage] ⏳ Waiting for requirements:', { tagSlug: !!tagSlug, isInitialized });
      return;
    }
    
    const loadTagData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // ✅ FIXED: Get language category for filtering AFTER ensuring initialization
        const languageCategoryId = getCurrentLanguageCategoryId();
        
        // 🚨 CRITICAL DEBUG: Log all language detection details
        console.log('[TagPage] 🚨 CRITICAL DEBUG - Language Detection Status:', {
          tagSlug,
          isMobile,
          perPage,
          languageCategoryId,
          isInitialized,
          languageContext,
          browserLanguages: navigator.languages ? Array.from(navigator.languages) : [navigator.language],
          expectedVietnameseId: '1',
          expectedEnglishId: '11703',
          currentDetectedId: languageCategoryId,
          isFilteringExpected: !!languageCategoryId
        });
        
        // 🚨 ALERT USER IF NO LANGUAGE FILTERING
        if (!languageCategoryId) {
          console.error('[TagPage] 🚨 CRITICAL ISSUE: No language filtering detected!');
          console.error('[TagPage] 🚨 This means ALL posts will be shown regardless of language!');
        }
        
        // First get tag info by slug (OPTIMIZED - no massive tag loading!)
        const tagData = await tagService.getTagBySlug(tagSlug);
        
        if (!tagData) {
          setError('Tag not found');
          return;
        }
        
        // Then load posts using tag ID with language filtering
        console.log('[TagPage] 🔄 About to fetch posts with language filtering:', {
          tagId: tagData.id,
          languageCategoryId,
          isLanguageFiltering: !!languageCategoryId
        });
        
        let postsData = await tagService.getPostsByTag(tagData.id, 1, perPage, false, languageCategoryId || undefined);
        
        console.log('[TagPage] 📄 Posts fetched - analyzing language filtering results:', {
          totalPostsFetched: postsData.length,
          languageCategoryId,
          expectedOnlyLanguageSpecificPosts: !!languageCategoryId,
          samplePostCategories: postsData.slice(0, 3).map(p => ({ id: p.id, categories: p.categories }))
        });
        
        // 🚨 CRITICAL FIX: If language filtering returns 0 posts, try without language filter as fallback
        let accurateCount = await tagService.getTagCount(tagSlug, languageCategoryId || undefined);
        
        if (postsData.length === 0 && languageCategoryId) {
          console.warn('[TagPage] ⚠️ Language filtering returned 0 posts, trying fallback without language filter...');
          
          // Try without language filtering as fallback
          const fallbackPostsData = await tagService.getPostsByTag(tagData.id, 1, perPage, false, undefined);
          const fallbackCount = await tagService.getTagCount(tagSlug, undefined);
          
          console.log('[TagPage] 🔄 Fallback results:', {
            fallbackPostsCount: fallbackPostsData.length,
            fallbackCount,
            usingFallback: fallbackPostsData.length > 0
          });
          
          if (fallbackPostsData.length > 0) {
            postsData = fallbackPostsData;
            accurateCount = fallbackCount;
            console.log('[TagPage] ✅ Using fallback data without language filtering');
          }
        }
        
        console.log('[TagPage] 🔢 Tag count API response:', {
          accurateCount,
          languageCategoryId,
          shouldBeLanguageFiltered: !!languageCategoryId
        });
        
        console.log('[TagPage] ✅ Data loaded successfully:', { 
          tag: tagData, 
          postsCount: postsData.length,
          accurateCount: accurateCount,
          hasMore: postsData.length === perPage,
          languageFilter: languageCategoryId || 'none',
          languageInitialized: isInitialized,
          isProperlyFiltered: !!languageCategoryId
        });
        
        setTag(tagData);
        setPosts(postsData);
        setTotalCount(accurateCount); // ✅ NEW: Set accurate count
        setHasMore(postsData.length === perPage);
        setPage(1);
        
      } catch (err) {
        console.error('[TagPage] ❌ Error loading tag data:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };
    
    loadTagData();
  }, [tagSlug, isMobile, perPage, isInitialized, getCurrentLanguageCategoryId]);

  // Load more posts
  const loadMorePosts = useCallback(async () => {
    if (loadingMore || !hasMore || !tag) {
      console.log('[TagPage] 🚫 LoadMore cancelled:', { loadingMore, hasMore, tag: !!tag });
      return;
    }
    
    try {
      setLoadingMore(true);
      const nextPage = page + 1;
      
      console.log('[TagPage] 📄 Loading more posts:', { 
        currentPage: page, 
        nextPage, 
        currentPostsCount: posts.length 
      });
      
      // ✅ NEW: Apply language filtering to load more (with fallback)
      const languageCategoryId = getCurrentLanguageCategoryId();
      let newPosts = await tagService.getPostsByTag(tag.id, nextPage, perPage, false, languageCategoryId || undefined);
      
      // 🚨 FALLBACK: If language filtering returns 0 posts, try without language filter
      if (newPosts.length === 0 && languageCategoryId) {
        console.warn('[TagPage] ⚠️ LoadMore: Language filtering returned 0 posts, trying fallback...');
        newPosts = await tagService.getPostsByTag(tag.id, nextPage, perPage, false, undefined);
        console.log('[TagPage] 🔄 LoadMore fallback result:', { fallbackPostsCount: newPosts.length });
      }
      
      console.log('[TagPage] ✅ More posts loaded:', { 
        newPostsCount: newPosts.length,
        totalPostsCount: posts.length + newPosts.length,
        hasMore: newPosts.length === perPage,
        languageFilter: languageCategoryId || 'none'
      });
      
      setPosts(prev => [...prev, ...newPosts]);
      setHasMore(newPosts.length === perPage);
      setPage(nextPage);
      
    } catch (err) {
      console.error('[TagPage] ❌ Error loading more posts:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [tag, page, posts.length, loadingMore, hasMore, perPage, getCurrentLanguageCategoryId]);

  // Handle post click - ENHANCED with tag context injection for mobile
  const handlePostClick = (post: WordPressPost) => {
    console.log('[TagPage] 🔗 Post clicked:', { 
      postId: post.id, 
      postSlug: post.slug,
      isMobile,
      tagContext: tag ? { id: tag.id, name: tag.name, slug: tag.slug } : null
    });
    
    if (isMobile && tag) {
      // 🎯 MOBILE: Inject tag context để user có thể xem tất cả bài viết cùng tag
      console.log('[TagPage] 📱 Mobile navigation WITH TAG CONTEXT injection');
      console.log('[TagPage] 📱 Injecting tag context:', {
        tagId: tag.id,
        tagName: tag.name,
        tagSlug: tag.slug,
        sourcePostId: post.id,
        totalTagPosts: posts.length
      });
      
      // Navigate với tag context trong navigation state
      navigate(`/post/${post.id}/${post.slug}.html`, {
        state: {
          tagContext: {
            tagId: tag.id,
            tagName: tag.name,
            tagSlug: tag.slug,
            sourcePostId: post.id,
            // Tiêm thêm info về danh sách posts để TikTokFeed có thể sử dụng
            tagPosts: posts.slice(0, 20), // Gửi 20 posts đầu tiên để khởi tạo nhanh
            totalTagPosts: posts.length
          }
        }
      });
    } else {
      // 💻 PC: Navigate thường hoặc mobile không có tag context
      console.log('[TagPage] 💻 Standard navigation (PC or no tag context)');
      navigate(`/post/${post.id}/${post.slug}.html`);
    }
  };

  // Enhanced close function with simple and reliable navigation
  const handleEnhancedClose = () => {
    console.log('[TagPage] 🔙 Enhanced close triggered');
    
    // Start close animation
    setIsVisible(false);
    
    // Show transition overlay to cover navigation frame
    setTimeout(() => {
      setShowNavigationOverlay(true);
    }, 200);
    
    // Simple and reliable navigation
    setTimeout(() => {
      console.log('[TagPage] 📍 Navigating back with history.back()');
      
      // Use browser back for smooth transition
      if (window.history.length > 1) {
        window.history.back();
      } else {
        // Fallback to home if no history
        navigate('/');
      }
    }, 250);
  };

  // Dynamic title and description
  const cleanTitle = (html: string): string => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || '';
  };

  const handleFinished = () => {
    console.log('[TagPage] 🏁 TikTok-style feed finished, redirecting to home');
    navigate('/');
  };

  // Handle loading states with skeleton UI - FIXED LOGIC
  if (loading) {
    return (
      <>
        <Helmet>
          <title>Loading... - Infonium</title>
          <meta name="description" content="Loading content..." />
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        
        {isMobile ? (
          <MobileGridSkeleton 
            count={30} 
            showHeader={true}
            headerTitle={tag ? `#${tag.name}` : "Loading..."}
          />
        ) : (
          <DesktopGridSkeleton count={12} />
        )}
      </>
    );
  }

  // Error state
  if (error || !tag) {
    return (
      <ErrorContainer>
        <div style={{ fontSize: '18px', marginBottom: '20px' }}>
          {error || 'Tag not found'}
        </div>
        <button 
          onClick={() => navigate('/')}
          style={{ 
            padding: '12px 24px', 
            background: '#ff4444', 
            border: 'none', 
            color: 'white', 
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '16px'
          }}
        >
                      ← Go Back to Homepage
        </button>
      </ErrorContainer>
    );
  }

  // 🔢 FIXED: Smart count display - use actual count or fallback to posts length
  const displayCount = totalCount > 0 ? totalCount : posts.length;
  
  // Render based on device type and context
  if (isMobile) {
    // Mobile: Grid layout with infinite scroll
    console.log('[TagPage] 📱 Rendering Mobile Grid layout');
    console.log('[TagPage] 🔢 Count display logic:', { totalCount, postsLength: posts.length, displayCount });
    return (
      <>
        <Helmet>
                  <title>#{tag.name} - Infonium | {displayCount} articles</title>
        <meta name="description" content={`View all articles about ${tag.name} on Infonium. Mobile interface with optimized grid layout, ${displayCount} quality articles.`} />
        <meta name="keywords" content={`${tag.name}, tag, ${tag.slug}, mobile, grid, infonium, articles, content`} />
          <meta name="author" content="Infonium" />
          <meta property="og:title" content={`#${tag.name} - Infonium`} />
          <meta property="og:description" content={`View all articles about ${tag.name} on Infonium. ${displayCount} quality articles.`} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={typeof window !== 'undefined' ? window.location.href : `https://www.infonium.org/tag/${tag.slug}`} />
          <meta property="og:image" content="https://cdn.infonium.org/img/logo_infonium.png" />
          <meta property="og:site_name" content="Infonium" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={`#${tag.name} - Infonium`} />
          <meta name="twitter:description" content={`View all articles about ${tag.name} on Infonium. ${displayCount} quality articles.`} />
          <meta name="twitter:image" content="https://cdn.infonium.org/img/logo_infonium.png" />
          <meta name="twitter:site" content="@infonium" />
          <link rel="canonical" href={typeof window !== 'undefined' ? window.location.href : `https://www.infonium.org/tag/${tag.slug}`} />
          <meta name="robots" content="index, follow" />
          <meta name="language" content="vi" />
        </Helmet>
        <div data-testid="tag-page">
          <MobileTagGrid
            tag={tag}
            posts={posts}
            loading={loading}
            loadingMore={loadingMore}
            hasMore={hasMore}
            totalCount={displayCount}
            onLoadMore={loadMorePosts}
            onPostClick={handlePostClick}
            onClose={handleEnhancedClose}
            isVisible={isVisible}
          />
        </div>
        
        {/* Navigation overlay to cover transition frame */}
        <NavigationOverlay isVisible={showNavigationOverlay} />
      </>
    );
  } else {
    // PC: NewsFeed style layout (COPIED FROM NEWSFEED)
    console.log('[TagPage] 💻 Rendering PC layout with PCTagFeed');
    console.log('[TagPage] 💻 Props for PCTagFeed:', {
      tag: tag?.name, 
      postsCount: posts.length, 
      loading, 
      loadingMore, 
      hasMore
    });
    console.log('[TagPage] 🔢 PC Count display logic:', { totalCount, postsLength: posts.length, displayCount });
    
    return (
      <>
        <Helmet>
          <title>#{tag.name} - Infonium | {displayCount} articles</title>
                  <meta name="description" content={`Read all articles about ${tag.name} on Infonium. Desktop interface with professional news feed layout, ${displayCount} high-quality articles.`} />
        <meta name="keywords" content={`${tag.name}, tag, ${tag.slug}, desktop, news feed, infonium, news, articles`} />
          <meta name="author" content="Infonium" />
          <meta property="og:title" content={`#${tag.name} - Infonium Desktop`} />
          <meta property="og:description" content={`Read all articles about ${tag.name} on Infonium. ${displayCount} high-quality articles.`} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={typeof window !== 'undefined' ? window.location.href : `https://www.infonium.org/tag/${tag.slug}`} />
          <meta property="og:image" content="https://cdn.infonium.org/img/logo_infonium.png" />
          <meta property="og:site_name" content="Infonium" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={`#${tag.name} - Infonium Desktop`} />
          <meta name="twitter:description" content={`Read all articles about ${tag.name} on Infonium. ${displayCount} high-quality articles.`} />
          <meta name="twitter:image" content="https://cdn.infonium.org/img/logo_infonium.png" />
          <meta name="twitter:site" content="@infonium" />
          <link rel="canonical" href={typeof window !== 'undefined' ? window.location.href : `https://www.infonium.org/tag/${tag.slug}`} />
          <meta name="robots" content="index, follow" />
          <meta name="language" content="vi" />
        </Helmet>
        <div data-testid="tag-page">
          <PCTagFeed
            tag={tag}
            posts={posts}
            loading={loading}
            loadingMore={loadingMore}
            hasMore={hasMore}
            totalCount={displayCount}
            onLoadMore={loadMorePosts}
            onPostClick={handlePostClick}
            onClose={handleEnhancedClose}
          />
        </div>
      </>
    );
  }
};

export default TagPage; 